// New visit at a chosen time: pick a patient, type a new name, or add the patient later.
import React, { useState } from 'react';
import { clockLabel, SNAP_MIN } from './visitsDay';

const fullName = (p) =>
  [p.firstName, p.lastName].filter(Boolean).join(' ').trim();

// "2:30 pm", "230", "14:30", "2 30" — whatever a busy hand types.
function parseTime(txt) {
  const t = String(txt).trim().toLowerCase();
  if (!t) return null;
  const ap = /(a|p)m?\s*$/.exec(t);
  const d = t.replace(/[^0-9]/g, '');
  if (!d) return null;
  let h = parseInt(d.length <= 2 ? d : d.slice(0, -2), 10);
  const mi = d.length <= 2 ? 0 : parseInt(d.slice(-2), 10);
  if (Number.isNaN(h) || Number.isNaN(mi) || mi > 59) return null;
  if (ap) h = (h % 12) + (ap[1] === 'p' ? 12 : 0);
  else if (h < 7) h += 12; // a clinic "1:30" means the afternoon
  if (h > 23) return null;
  return h * 60 + mi;
}

const AddVisitSheet = ({
  day,
  startMin,
  durationMin,
  patients,
  onClose,
  onCreate,
}) => {
  const [query, setQuery] = useState('');
  const [min, setMin] = useState(startMin);
  const [timeText, setTimeText] = useState(clockLabel(startMin));
  const [timeOpen, setTimeOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const q = query.trim().toLowerCase();
  const hits = q
    ? (patients || [])
        .filter((p) => fullName(p).toLowerCase().includes(q))
        .slice(0, 6)
    : [];
  const exact = hits.some((p) => fullName(p).toLowerCase() === q);

  const setTime = (m) => {
    const next = Math.max(0, Math.min(24 * 60 - SNAP_MIN, m));
    setMin(next);
    setTimeText(clockLabel(next));
  };
  const commitText = () => {
    const parsed = parseTime(timeText);
    setTime(parsed === null ? min : parsed);
  };

  const create = async (visit) => {
    if (busy) return;
    setBusy(true);
    try {
      await onCreate({ ...visit, startMin: min, durationMin });
    } finally {
      setBusy(false);
    }
  };

  const hourStart = Math.floor(min / 60) * 60;
  const dayLong = day.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div
      className="vs-sheet-wrap"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="vs-sheet">
        <div className="vs-sheet-head">
          <div>
            <h3>New visit</h3>
            <p className="vs-sub">
              {dayLong}
              {!timeOpen && (
                <>
                  {' · '}
                  <b>{clockLabel(min)}</b>
                </>
              )}{' '}
              <button
                type="button"
                className="vs-whenedit"
                aria-expanded={timeOpen}
                onClick={() => setTimeOpen(!timeOpen)}
              >
                {timeOpen ? 'Done' : 'Change time'}
              </button>
            </p>
          </div>
          <span className="vs-sp" />
          <button
            type="button"
            className="vs-sheetx"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {timeOpen && (
          <div className="vs-timeset">
            <button
              type="button"
              className="vs-tstep"
              aria-label="15 minutes earlier"
              onClick={() => setTime(min - SNAP_MIN)}
            >
              ‹
            </button>
            <input
              className="vs-tval"
              type="text"
              autoComplete="off"
              spellCheck="false"
              aria-label="Visit time"
              value={timeText}
              onChange={(e) => setTimeText(e.target.value)}
              onBlur={commitText}
              onKeyDown={(e) => e.key === 'Enter' && commitText()}
            />
            <button
              type="button"
              className="vs-tstep"
              aria-label="15 minutes later"
              onClick={() => setTime(min + SNAP_MIN)}
            >
              ›
            </button>
            <span className="vs-mins">
              {[0, 15, 30, 45].map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`vs-minb${min % 60 === m ? ' vs-on' : ''}`}
                  aria-pressed={min % 60 === m}
                  aria-label={clockLabel(hourStart + m)}
                  onClick={() => setTime(hourStart + m)}
                >
                  {`:${String(m).padStart(2, '0')}`}
                </button>
              ))}
            </span>
          </div>
        )}
        <input
          className="vs-psearch"
          type="text"
          autoFocus
          autoComplete="off"
          placeholder="Search patients"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="vs-plist">
          {!q && <p className="vs-pnone">Start typing a patient’s name.</p>}
          {q && !hits.length && (
            <p className="vs-pnone">No match in your patients.</p>
          )}
          {hits.map((p) => (
            <button
              key={p.uid}
              type="button"
              className="vs-pitem"
              disabled={busy}
              onClick={() => create({ patientId: p.uid })}
            >
              {fullName(p)}
            </button>
          ))}
          {q && !exact && (
            <button
              type="button"
              className="vs-pitem vs-new"
              disabled={busy}
              onClick={() => create({ name: query.trim() })}
            >
              {`+ Add “${query.trim()}”`}
            </button>
          )}
        </div>
        <button
          type="button"
          className="vs-laterb"
          disabled={busy}
          onClick={() => create({})}
        >
          Add patient later
        </button>
      </div>
    </div>
  );
};

export default AddVisitSheet;
